import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  StatusCell,
  PaginationTable,
  DateCell,
  RowLink,
  BOPaginationTable,
  SwitchTableVariantButtons,
} from 'common/components/index';
import { useToggleTableVariant } from 'common/hooks';
import { getNewTableColumns } from 'common/utils';

import { withTheme } from 'common/styling/theme';
import { staticStyles, getDynamicStyles } from './style';

const TransfersTable = ({
  data,
  pageLink,
  onRowClick,
  getList,
  loading,
  history,
  location,
  params,
  theme,
  intl,
  hasPaymentOperationManagers,
  withNewTableVariant,
}) => {
  const TRANSFER_TYPE_SLUGS = {
    INNER: 'boInnerTransfer',
    TO_TRADER: 'boAnotherTrader',
    FROM_WALLET: 'fromWallet',
    FROM_WALLET_ACCOUNT: 'fromWalletAccount',
    TO_WALLET_ACCOUNT: 'toWalletAccount',
    TO_WALLET: 'toWallet',
    TO_TRADER_WALLET_ACCOUNT: 'toTraderWalletAccount',
    TO_TRADER_WALLET: 'toTraderWallet',
    TO_TRADER_LPA: 'justToLpa',
    TO_TRADER_WALLET_LPA: 'justToWalletLpa',
    INVEST_INNER: 'justInvestInner',
    TO_WALLET_INVEST: 'justToWalletInvest',
    FROM_WALLET_INVEST: 'justFromWalletInvest',
    FROM_WALLET_ACCOUNT_INVEST: 'justFromWalletAccountInvest',
    TO_WALLET_ACCOUNT_INVEST: 'justToWalletAccountInvest',
    INVEST_REWARD: 'justInvestReward',
  };

  const { tableVariant, handleSelectNewVariant, handleSelectLegacyVariant } = useToggleTableVariant({
    tableKey: 'BOTransfersTable',
  });

  /* eslint-disable */
  const columns = [
    {
      Header: 'boId',
      accessor: 'id',
      size: 100,
      Cell: ({
        row: {
          original: { id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`} isPreviewCell>
          <span className="TransfersTable__sender-id">{id}</span>
        </RowLink>
      ),
    },
    {
      Header: 'accountFrom',
      accessor: 'senderAccount',
      Cell: ({
        row: {
          original: { senderAccount, email, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`} isPreviewCell>
          <span className="TransfersTable__sender-account">
            {senderAccount || email || intl.formatMessage({ id: 'menuWallet' })}
          </span>
        </RowLink>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'accountTo',
      accessor: 'recipientAccount',
      Cell: ({
        row: {
          original: { recipientAccount, recipient, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`}>
          <span className="TransfersTable__sender-account">
            {recipientAccount || recipient || intl.formatMessage({ id: 'menuWallet' })}
          </span>
        </RowLink>
      ),
    },
    {
      Header: 'boStatus',
      accessor: 'status',
      size: 200,
      Cell: ({
        row: {
          original: { status, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`} isPreviewCell>
          <StatusCell statusCode={status} />
        </RowLink>
      ),
      isPreviewCell: true,
    },
    {
      Header: 'boAmount',
      Cell: ({
        row: {
          original: { sendAmount, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`} isPreviewCell>
          <span className="TransfersTable__amount">
            {sendAmount.amount} {sendAmount.currency}
          </span>
        </RowLink>
      ),
      isPreviewCell: true,
    },
    ...(hasPaymentOperationManagers
      ? [
          {
            Header: 'justManager',
            Cell: ({
              row: {
                original: { manager, id },
              },
            }) => (
              <RowLink link={`/${pageLink}/${id}`}>
                <div>{manager ? `${manager.firstName} ${manager.lastName}` : '一'}</div>
              </RowLink>
            ),
          },
        ]
      : []),
    {
      Header: 'boTransferType',
      size: 200,
      Cell: ({
        row: {
          original: { transferType, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`}>
          {TRANSFER_TYPE_SLUGS[transferType] ? (
            <FormattedMessage id={TRANSFER_TYPE_SLUGS[transferType]}>
              {txt => <span className="TransfersTable__transferType">{txt}</span>}
            </FormattedMessage>
          ) : (
            <span className="TransfersTable__transferType" />
          )}
        </RowLink>
      ),
    },
    {
      Header: 'boCreated',
      accessor: 'created',
      size: 350,
      Cell: ({
        row: {
          original: { created, id },
        },
      }) => (
        <RowLink link={`/${pageLink}/${id}`}>
          <DateCell value={created} />
        </RowLink>
      ),
    },
  ];
  /* eslint-enable */

  const newTableColumns = useMemo(() => getNewTableColumns(columns), []);

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  return (
    <div className="TransfersTable">
      {withNewTableVariant && (
        <div className="TransfersTable--with-switch-variants">
          <SwitchTableVariantButtons
            tableVariant={tableVariant}
            handleSelectNewVariant={handleSelectNewVariant}
            handleSelectLegacyVariant={handleSelectLegacyVariant}
          />

          {tableVariant === 'legacy' && !loading && (
            <PaginationTable
              data={data}
              loading={loading}
              columns={columns}
              onRowClick={onRowClick}
              getList={getList}
              location={location}
              history={history}
              params={params}
            />
          )}

          {tableVariant === 'new' && (
            <BOPaginationTable
              tableKey="BOTransfersTable"
              data={data}
              columns={newTableColumns}
              params={params}
              getList={getList}
              loading={loading}
            />
          )}
        </div>
      )}

      {!withNewTableVariant && (
        <PaginationTable
          data={data}
          loading={loading}
          columns={columns}
          onRowClick={onRowClick}
          getList={getList}
          location={location}
          history={history}
          params={params}
        />
      )}

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

TransfersTable.propTypes = {
  onRowClick: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  pageLink: PropTypes.string.isRequired,
  getList: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  theme: PropTypes.object,
  hasPaymentOperationManagers: PropTypes.bool.isRequired,
  withNewTableVariant: PropTypes.bool,
};

TransfersTable.defaultProps = {
  withNewTableVariant: false,
  theme: {},
};

export default compose(injectIntl, withTheme())(TransfersTable);
export { TransfersTable };
